import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumbs from "./Breadcrumbs";
import JsonLd from "./JsonLd";
import { CATEGORIES } from "../lib/categories";
import categoryHubs from "../data/categoryHubs.json";

const SITE_URL = "https://pawground.app";

// Per-category intro paragraph for SEO content depth — hand-written
// so each hub has unique copy (Google penalizes near-duplicate H1+intro
// blocks across thousands of pages). Lookup keyed by category.key.
const HUB_INTRO = {
  park:       "Find off-leash dog parks with real reviews from local owners. Filter by city to see fenced areas, hours, and live check-ins from dogs visiting right now.",
  beach:      "Discover dog-friendly beaches where pups can swim, run, and play in the sand. Each listing notes leash rules, seasonal restrictions, and parking details.",
  restaurant: "Browse pet-friendly restaurants and bars with patio seating, water bowls, and house pups welcome. Sorted by city so you can find your next dinner spot.",
  vet:        "Locate trusted veterinarians and animal hospitals near you. See ratings, emergency hours, and what fellow dog parents say about each clinic.",
  groomer:    "Find dog groomers offering full grooms, baths, nail trims, and breed-specific styling. Reviews from local owners help you pick the right one.",
  boarding:   "Compare dog boarding and daycare facilities — overnight stays, daily play, and luxury suites. Each listing shows pricing, hours, and visitor reviews.",
  trainer:    "Connect with certified dog trainers for puppy classes, obedience, agility, and behavior work. Read owner reviews before you book.",
  store:      "Find pet stores stocked with food, toys, treats, and supplies. Most welcome leashed dogs inside — listings note which ones do.",
  trail:      "Explore dog-friendly hiking trails and walking paths. Each listing shows leash policy, terrain, difficulty, and what local owners think.",
};

const TOP_N = 12;

const CategoryHub = ({ category, urlSlug }) => {
  const allCities = useMemo(() => categoryHubs[category.key] || [], [category.key]);
  const top = allCities.slice(0, TOP_N);
  const rest = allCities.slice(TOP_N);
  const [expanded, setExpanded] = useState(false);

  // Other 8 category links for cross-linking at the bottom
  const otherCats = CATEGORIES.filter((c) => c.key !== category.key);

  const intro = HUB_INTRO[category.key] || "";

  const restAlpha = useMemo(
    () => [...rest].sort((a, b) => a.city.localeCompare(b.city)),
    [rest],
  );

  // ItemList JSON-LD for the directory itself — gives Google a
  // structured map of every city link on the page.
  const schema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `${category.seoLabel} by city`,
    numberOfItems: allCities.length,
    itemListElement: top.map((row, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: `${category.seoShort} in ${row.city}, ${row.state}`,
      url: `${SITE_URL}/${urlSlug}/${row.slug}`,
    })),
  };

  return (
    <div className="bg-brand-cream min-h-[calc(100vh-4rem)]" data-testid="category-hub">
      <JsonLd schema={schema} id="category-hub-itemlist" />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <Breadcrumbs items={[{ name: category.seoLabel }]} />

        <h1
          className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black mt-4 leading-[1.05] tracking-tight text-brand-ink"
          data-testid="category-hub-h1"
        >
          {category.seoLabel} Near You
        </h1>

        {intro && (
          <p
            className="mt-4 text-base sm:text-lg text-brand-ink/75 max-w-3xl leading-relaxed"
            data-testid="category-hub-intro"
          >
            {intro}
          </p>
        )}

        {allCities.length === 0 ? (
          <p className="mt-8 text-brand-ink/60">Directory loading — check back soon.</p>
        ) : (
          <>
            <section className="mt-10">
              <h2 className="font-heading text-2xl font-bold text-brand-ink mb-4">Top cities</h2>
              <ul
                className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
                data-testid="category-hub-top-cities"
              >
                {top.map((row) => (
                  <li key={row.slug}>
                    <a
                      href={`/${urlSlug}/${row.slug}`}
                      className="block bg-white border border-brand-border-warm rounded-2xl p-4 hover:border-brand-forest hover:shadow-warm transition-all"
                      data-testid={`hub-city-${row.slug}`}
                    >
                      <div className="font-heading text-base font-bold text-brand-ink leading-tight">
                        {row.city}, {row.state}
                      </div>
                      <div className="text-xs text-brand-forest font-semibold mt-1">
                        {row.count} places
                      </div>
                    </a>
                  </li>
                ))}
              </ul>
            </section>

            {restAlpha.length > 0 && (
              <section className="mt-10">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-2xl font-bold text-brand-ink">All cities</h2>
                  <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    className="text-sm font-semibold text-brand-forest hover:underline"
                    data-testid="category-hub-toggle-all"
                  >
                    {expanded ? "Hide" : `Show all ${restAlpha.length}`}
                  </button>
                </div>
                {/* Always render the full list in the DOM so Googlebot
                    crawls every city link — visually hide when not
                    expanded via inline style (display:none would also
                    hide from crawlers; we want the markup present). */}
                <ul
                  className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-1.5 text-sm"
                  data-testid="category-hub-all-cities"
                  style={expanded ? {} : { maxHeight: 0, overflow: "hidden", opacity: 0 }}
                  aria-hidden={!expanded}
                >
                  {restAlpha.map((row) => (
                    <li key={row.slug}>
                      <a
                        href={`/${urlSlug}/${row.slug}`}
                        className="text-brand-ink/80 hover:text-brand-forest hover:underline"
                      >
                        {row.city}, {row.state}{" "}
                        <span className="text-xs text-brand-ink/40">({row.count})</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}

        <section className="mt-12 pt-8 border-t border-brand-border-warm">
          <h2 className="font-heading text-2xl font-bold text-brand-ink mb-4">
            Browse other categories
          </h2>
          <ul
            className="flex flex-wrap gap-2"
            data-testid="category-hub-other-categories"
          >
            {otherCats.map((c) => (
              <li key={c.key}>
                <a
                  href={`/${c.urlSlug}`}
                  className="inline-flex items-center bg-white border border-brand-border-warm rounded-full px-4 py-2 text-sm font-semibold text-brand-ink hover:border-brand-forest hover:text-brand-forest transition-colors"
                  data-testid={`hub-other-${c.key}`}
                >
                  {c.seoLabel}
                </a>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
};

export default CategoryHub;
